import React, { useState, useEffect, useRef } from 'react';
import { playPhaseStart, playWarning, playTimeUp, playClick, playVictory } from '../sounds';

const ACCENT = '#E040FB';
const ACCENT_LIGHT = 'rgba(224,64,251,0.12)';
const MUTED = '#c4b5d9';
const GOLD = '#FFD700';
const GREEN = '#4CAF50';

const CARD_ICONS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

function Timer({ seconds, warning }) {
  return (
    <div className="timer-container">
      <div className={`timer-ring ${warning ? 'warning' : ''}`}>{seconds}</div>
    </div>
  );
}

function Complete({ gameState, playerId, roomCode, sendMessage, error, clearError }) {
  const {
    phase, currentRound, totalRounds, phaseTimeLeft, prompt,
    hasSubmitted, submittedCount, totalPlayers,
    submissions, hasVoted, votedCount,
    winnerIdx, players,
  } = gameState;

  const [localTimer, setLocalTimer] = useState(phaseTimeLeft || 0);
  const [input, setInput] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [revealedCards, setRevealedCards] = useState([]);
  const inputRef = useRef(null);
  const isHost = players?.find(p => p.id === playerId)?.isHost;

  // Sync timer from server
  useEffect(() => {
    setLocalTimer(phaseTimeLeft || 0);
  }, [phaseTimeLeft, phase]);

  // Countdown
  useEffect(() => {
    if (phase === 'reveal' || phase === 'final_results') return;
    const interval = setInterval(() => {
      setLocalTimer(prev => {
        const next = Math.max(0, prev - 1);
        if (next === 5 || next === 3) playWarning();
        if (next === 0 && prev > 0) playTimeUp();
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase]);

  // Phase transitions
  useEffect(() => {
    setInput('');
    setSelectedIndex(null);
    setRevealedCards([]);
    if (phase === 'writing') {
      playPhaseStart();
      setTimeout(() => inputRef.current?.focus(), 200);
    } else if (phase === 'voting') {
      playPhaseStart();
    } else if (phase === 'reveal') {
      playPhaseStart();
    } else if (phase === 'final_results') {
      playVictory();
    }
  }, [phase, currentRound]);

  // Reveal animation: flip cards one by one
  useEffect(() => {
    if (phase !== 'reveal' || !submissions) return;
    setRevealedCards([]);
    let i = 0;
    const interval = setInterval(() => {
      if (i >= submissions.length) {
        clearInterval(interval);
        return;
      }
      setRevealedCards(prev => [...prev, i]);
      i++;
    }, 600);
    return () => clearInterval(interval);
  }, [phase, submissions?.length, currentRound]);

  const submitText = () => {
    if (!input.trim()) return;
    playClick();
    sendMessage('COMPLETE_SUBMIT', { text: input.trim() });
    setInput('');
  };

  const submitVote = () => {
    if (selectedIndex === null) return;
    playClick();
    sendMessage('COMPLETE_VOTE', { index: selectedIndex });
  };

  // ===== WRITING PHASE =====
  if (phase === 'writing') {
    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <Timer seconds={localTimer} warning={localTimer <= 5} />

        <div style={{ textAlign: 'center', marginBottom: '8px' }}>
          <p style={{ fontSize: '0.8rem', color: MUTED }}>
            סיבוב {currentRound} מתוך {totalRounds}
          </p>
        </div>

        <h2 style={{
          textAlign: 'center', fontSize: '1.1rem', marginBottom: '16px',
          color: ACCENT,
        }}>
          מה ההמשך? ✍️
        </h2>

        {/* Prompt */}
        <div style={{
          padding: '20px', borderRadius: '16px', marginBottom: '20px',
          background: 'linear-gradient(135deg, rgba(224,64,251,0.08), rgba(255,255,255,0.03))',
          border: '1px solid rgba(224,64,251,0.25)',
          textAlign: 'center',
        }}>
          <p style={{ fontSize: '1.2rem', lineHeight: 1.7, fontWeight: 600 }}>{prompt}</p>
        </div>

        {!hasSubmitted ? (
          <>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value.slice(0, 100))}
              placeholder="השלימו את המשפט..."
              maxLength={100}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitText(); } }}
              style={{
                width: '100%', minHeight: '70px', padding: '14px', borderRadius: '14px',
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)',
                color: 'white', fontSize: '1rem', fontFamily: 'Heebo, sans-serif',
                resize: 'none', direction: 'rtl', marginBottom: '8px', boxSizing: 'border-box',
              }}
            />
            <p style={{ textAlign: 'left', fontSize: '0.7rem', color: MUTED, marginBottom: '10px' }}>
              {input.length}/100
            </p>
            <button
              className="btn btn-primary"
              onClick={submitText}
              disabled={!input.trim()}
              style={{ background: ACCENT, borderColor: ACCENT, width: '100%' }}
            >
              שלח
            </button>
          </>
        ) : (
          <div style={{
            textAlign: 'center', padding: '24px',
            background: 'rgba(76,175,80,0.1)', borderRadius: '14px',
          }}>
            <p style={{ color: GREEN, fontWeight: 700, fontSize: '1.1rem' }}>
              &#10003; נשלח!
            </p>
            <p style={{ color: MUTED, fontSize: '0.85rem', marginTop: '10px' }}>
              ממתינים ל-{totalPlayers - (submittedCount || 0)} שחקנים...
            </p>
          </div>
        )}

        {error && <p className="error-msg">{error}</p>}
      </div>
    );
  }

  // ===== VOTING PHASE =====
  if (phase === 'voting') {
    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <Timer seconds={localTimer} warning={localTimer <= 5} />

        <div style={{ textAlign: 'center', marginBottom: '8px' }}>
          <p style={{ fontSize: '0.8rem', color: MUTED }}>
            סיבוב {currentRound} מתוך {totalRounds}
          </p>
        </div>

        <h2 style={{
          textAlign: 'center', fontSize: '1.1rem', marginBottom: '12px',
        }}>
          הצביעו להשלמה הכי מצחיקה!
        </h2>

        {/* Prompt reminder */}
        <div style={{
          padding: '12px 16px', borderRadius: '12px', marginBottom: '16px',
          background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
          textAlign: 'center',
        }}>
          <p style={{ fontSize: '0.95rem', color: MUTED }}>{prompt}</p>
        </div>

        {!hasVoted ? (
          <>
            <div style={{ display: 'grid', gap: '10px', marginBottom: '14px' }}>
              {submissions && submissions.map((s) => {
                const isOwn = s.isOwn;
                const isSelected = selectedIndex === s.index;
                return (
                  <button
                    key={s.index}
                    onClick={() => {
                      if (!isOwn) {
                        setSelectedIndex(s.index);
                        playClick();
                      }
                    }}
                    disabled={isOwn}
                    style={{
                      padding: '14px 16px', borderRadius: '14px', textAlign: 'right',
                      border: isSelected
                        ? `2px solid ${ACCENT}`
                        : isOwn
                          ? '2px solid rgba(255,255,255,0.05)'
                          : '2px solid rgba(255,255,255,0.12)',
                      background: isSelected
                        ? ACCENT_LIGHT
                        : isOwn
                          ? 'rgba(255,255,255,0.02)'
                          : 'rgba(255,255,255,0.05)',
                      color: isOwn ? 'rgba(255,255,255,0.35)' : 'white',
                      fontFamily: 'Heebo, sans-serif', cursor: isOwn ? 'default' : 'pointer',
                      fontSize: '0.95rem', lineHeight: 1.5, width: '100%', direction: 'rtl',
                      position: 'relative', transition: 'all 0.2s ease',
                      transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                    }}
                  >
                    <span style={{
                      display: 'inline-block', width: '28px', height: '28px',
                      borderRadius: '50%', textAlign: 'center', lineHeight: '28px',
                      fontSize: '0.75rem', fontWeight: 700, marginLeft: '10px',
                      background: isSelected ? ACCENT : 'rgba(255,255,255,0.1)',
                      color: isSelected ? 'white' : MUTED,
                      verticalAlign: 'middle',
                    }}>
                      {CARD_ICONS[s.index] || s.index + 1}
                    </span>
                    {s.text}
                    {isOwn && (
                      <span style={{
                        fontSize: '0.7rem', color: MUTED, display: 'block', marginTop: '4px',
                      }}>
                        (ההשלמה שלך)
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <button
              className="btn btn-primary"
              onClick={submitVote}
              disabled={selectedIndex === null}
              style={{ background: ACCENT, borderColor: ACCENT, width: '100%' }}
            >
              הצבע!
            </button>
          </>
        ) : (
          <div style={{
            textAlign: 'center', padding: '24px',
            background: 'rgba(76,175,80,0.1)', borderRadius: '14px',
          }}>
            <p style={{ color: GREEN, fontWeight: 700 }}>&#10003; הצבעת!</p>
            <p style={{ color: MUTED, fontSize: '0.85rem', marginTop: '8px' }}>
              {votedCount} מתוך {totalPlayers} הצביעו
            </p>
          </div>
        )}

        {error && <p className="error-msg">{error}</p>}
      </div>
    );
  }

  // ===== REVEAL PHASE =====
  if (phase === 'reveal') {
    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <div style={{ textAlign: 'center', marginBottom: '8px' }}>
          <p style={{ fontSize: '0.8rem', color: MUTED }}>
            סיבוב {currentRound} מתוך {totalRounds}
          </p>
        </div>

        <h2 style={{
          textAlign: 'center', fontSize: '1.2rem', marginBottom: '12px', color: GOLD,
        }}>
          והתשובות הן...
        </h2>

        {/* Prompt */}
        <div style={{
          padding: '12px 16px', borderRadius: '12px', marginBottom: '18px',
          background: 'rgba(255,255,255,0.03)', textAlign: 'center',
        }}>
          <p style={{ fontSize: '0.95rem', color: MUTED }}>{prompt}</p>
        </div>

        {/* Revealed cards */}
        <div style={{ display: 'grid', gap: '12px', marginBottom: '20px' }}>
          {submissions && submissions.map((s, i) => {
            const isRevealed = revealedCards.includes(i);
            return (
              <div
                key={s.index}
                style={{
                  padding: '14px 16px', borderRadius: '14px',
                  border: s.isWinner
                    ? `2px solid ${GOLD}`
                    : '2px solid rgba(255,255,255,0.1)',
                  background: s.isWinner
                    ? 'rgba(255,215,0,0.08)'
                    : 'rgba(255,255,255,0.04)',
                  opacity: isRevealed ? 1 : 0,
                  transform: isRevealed ? 'rotateX(0deg)' : 'rotateX(90deg)',
                  transition: 'all 0.5s ease',
                  transformOrigin: 'center center',
                }}
              >
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                }}>
                  <div style={{ flex: 1, direction: 'rtl' }}>
                    <p style={{
                      fontSize: '1rem', lineHeight: 1.5, marginBottom: '6px',
                      fontWeight: s.isWinner ? 700 : 500,
                      color: s.isWinner ? GOLD : 'white',
                    }}>
                      {s.isWinner && <span style={{ marginLeft: '6px' }}>&#127942;</span>}
                      {s.text}
                    </p>
                    <p style={{
                      fontSize: '0.85rem', color: ACCENT,
                      fontWeight: 600,
                    }}>
                      {s.avatar && <span style={{ marginLeft: '4px' }}>{s.avatar}</span>}
                      {s.playerName}
                    </p>
                  </div>
                  <div style={{
                    textAlign: 'center', minWidth: '50px', paddingRight: '10px',
                  }}>
                    <p style={{
                      fontSize: '1.4rem', fontWeight: 700,
                      color: s.votes > 0 ? GOLD : MUTED,
                    }}>
                      {s.votes}
                    </p>
                    <p style={{ fontSize: '0.65rem', color: MUTED }}>
                      {s.votes === 1 ? 'הצבעה' : 'הצבעות'}
                    </p>
                  </div>
                </div>
                {s.votes > 0 && (
                  <p style={{
                    fontSize: '0.7rem', color: GREEN, marginTop: '4px',
                  }}>
                    +{s.votes * 10} נק'
                    {s.isWinner && <span style={{ color: GOLD }}> +5 בונוס מנצח</span>}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Leaderboard mini */}
        <div className="result-section">
          <h3 style={{ fontSize: '0.9rem', color: MUTED, marginBottom: '8px' }}>ניקוד</h3>
          <div className="leaderboard">
            {players && [...players].sort((a, b) => b.score - a.score).map((p, i) => (
              <div key={p.id} className="leaderboard-item">
                <span className="leaderboard-rank">
                  {i === 0 ? '\uD83E\uDD47' : i === 1 ? '\uD83E\uDD48' : i === 2 ? '\uD83E\uDD49' : `${i + 1}.`}
                </span>
                <span className="leaderboard-name">
                  {p.avatar && <span style={{ marginLeft: '4px' }}>{p.avatar}</span>}
                  {p.name}
                  {p.id === playerId && <span className="me-badge"> (אני)</span>}
                </span>
                <span className="leaderboard-score">{p.score}</span>
              </div>
            ))}
          </div>
        </div>

        {error && <p className="error-msg">{error}</p>}

        <style>{`
          @keyframes flipIn {
            from { transform: rotateX(90deg); opacity: 0; }
            to { transform: rotateX(0deg); opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  // ===== FINAL RESULTS =====
  if (phase === 'final_results') {
    const sortedPlayers = players ? [...players].sort((a, b) => b.score - a.score) : [];
    const winner = sortedPlayers[0];

    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <h2 style={{
          textAlign: 'center', fontSize: '1.4rem', marginBottom: '8px', color: GOLD,
        }}>
          &#127942; תוצאות סופיות!
        </h2>

        {winner && (
          <div style={{
            textAlign: 'center', padding: '20px', marginBottom: '20px',
            background: 'linear-gradient(135deg, rgba(255,215,0,0.1), rgba(224,64,251,0.08))',
            borderRadius: '16px', border: `1px solid ${GOLD}40`,
          }}>
            <p style={{ fontSize: '2rem', marginBottom: '8px' }}>
              {winner.avatar || '\uD83C\uDFC6'}
            </p>
            <p style={{ fontSize: '1.2rem', fontWeight: 700, color: GOLD }}>
              {winner.name}
            </p>
            <p style={{ fontSize: '0.9rem', color: MUTED, marginTop: '4px' }}>
              {winner.score} נקודות
            </p>
          </div>
        )}

        {/* Full leaderboard */}
        <div className="result-section" style={{ marginBottom: '20px' }}>
          <h3>טבלת ניקוד סופית</h3>
          <div className="leaderboard">
            {sortedPlayers.map((p, i) => (
              <div key={p.id} className="leaderboard-item" style={{
                background: i === 0 ? 'rgba(255,215,0,0.08)' : 'transparent',
                borderRadius: '10px', padding: '8px 4px',
              }}>
                <span className="leaderboard-rank">
                  {i === 0 ? '\uD83E\uDD47' : i === 1 ? '\uD83E\uDD48' : i === 2 ? '\uD83E\uDD49' : `${i + 1}.`}
                </span>
                <span className="leaderboard-name">
                  {p.avatar && <span style={{ marginLeft: '4px' }}>{p.avatar}</span>}
                  {p.name}
                  {p.id === playerId && <span className="me-badge"> (אני)</span>}
                </span>
                <span className="leaderboard-score">{p.score}</span>
              </div>
            ))}
          </div>
        </div>

        {isHost && (
          <>
            <button
              className="btn btn-primary"
              onClick={() => { clearError(); sendMessage('NEXT_ROUND', {}); }}
              style={{ width: '100%', background: ACCENT, borderColor: ACCENT, marginBottom: '8px' }}
            >
              משחק חדש!
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => { clearError(); sendMessage('NEW_GAME', {}); }}
              style={{ width: '100%' }}
            >
              חזרה ללובי
            </button>
          </>
        )}
        {!isHost && <p className="waiting">ממתינים שהמנהל ימשיך...</p>}

        {error && <p className="error-msg">{error}</p>}
      </div>
    );
  }

  return <div className="card"><p style={{ textAlign: 'center', color: MUTED }}>טוען...</p></div>;
}

export default Complete;
